import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { DocumentRow, UploadChangePayload, UploadInput } from '@mallpay/orbis';
import { FIELD_RECEIPT, FIELD_PHOTOS, FIELD_POLICE_REPORT } from '../InsuranceReportForm.types';
import { StepSection } from '../StepSection';
import { components } from '@/api/skippay/generated/selfCare';
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
}
const mapUploadPayloadToFiles = async (payload: UploadChangePayload): Promise<components['schemas']['File'][]> => {
  if (payload && 'target' in payload) {
    const files: FileList | null = payload.target.files;
    if (!files) return [];
    return Promise.all(Array.from(files).map(async file => {
      const base64 = await fileToBase64(file);
      return {
        filename: file.name,
        size: file.size,
        contentType: file.type,
        content: base64,
        url: URL.createObjectURL(file)
      };
    }));
  }
  if (payload && 'filename' in payload) {
    const base64Source = payload.base64 && payload.type ? `data:${payload.type};base64,${payload.base64}` : payload.content;
    return [{
      filename: payload.filename,
      size: undefined,
      contentType: payload.type,
      content: base64Source,
      url: base64Source
    }];
  }
  return [];
};
const MAX_FILES = 4;
export const DocumentsStep = t0 => {
  const $ = _c(18);
  const {
    isTheft
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["insuranceReport", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("insuranceReport:form.documents.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = <Field name={FIELD_RECEIPT} validate={value => !value || value.length === 0 ? t("core:forms.error.required") : undefined}>{t4 => {
        const {
          input,
          meta
        } = t4;
        const submitError = !meta.dirtySinceLastSubmit ? meta.submitError : undefined;
        const errorMessage = meta.error || submitError;
        const hasError = !!errorMessage && (meta.touched || meta.submitFailed);
        return <div className="flex flex-col gap-3"><p className="mb-1 font-semibold">{t("insuranceReport:form.documents.fields.receipt")}</p><UploadInput mobAppBuild={undefined} inputStyle="uploadIcon" platform="WEB" dragAndDropStyle={false} accept="application/pdf,image/*" multiple={false} name={FIELD_RECEIPT} disabled={input.value?.length >= MAX_FILES} onChange={async payload => {
            const files = await mapUploadPayloadToFiles(payload);
            if (files.length) {
              const current = input.value || [];
              const remaining = MAX_FILES - current.length;
              if (remaining > 0) {
                input.onChange([...current, ...files.slice(0, remaining)]);
              }
            }
          }}>{t("insuranceReport:form.documents.fields.receipt")}</UploadInput>{input.value && input.value.map((doc, index) => <DocumentRow key={`${doc.filename}-${index}`} document={doc} onDelete={() => {
            const next = [...(input.value || [])];
            next.splice(index, 1);
            input.onChange(next);
          }} />)}{hasError && <p className="mt-1 text-sm text-red-500">{errorMessage}</p>}</div>;
      }}</Field>;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isTheft ? FIELD_POLICE_REPORT : FIELD_PHOTOS;
  let t5;
  if ($[5] !== t) {
    t5 = value_0 => !value_0 || value_0.length === 0 ? t("core:forms.error.required") : undefined;
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isTheft || $[8] !== t) {
    t6 = t7 => {
      const {
        input: input_0,
        meta: meta_0
      } = t7;
      const submitError_0 = !meta_0.dirtySinceLastSubmit ? meta_0.submitError : undefined;
      const errorMessage_0 = meta_0.error || submitError_0;
      const hasError_0 = !!errorMessage_0 && (meta_0.touched || meta_0.submitFailed);
      return <div className="flex flex-col gap-3"><p className="mb-1 font-semibold">{isTheft ? t("insuranceReport:form.documents.fields.policeReport") : t("insuranceReport:form.documents.fields.photos")}</p><UploadInput mobAppBuild={undefined} inputStyle="uploadIcon" platform="WEB" dragAndDropStyle={false} accept="application/pdf,image/*" multiple={true} name={isTheft ? FIELD_POLICE_REPORT : FIELD_PHOTOS} disabled={input_0.value?.length >= MAX_FILES} onChange={async payload_0 => {
          const files_0 = await mapUploadPayloadToFiles(payload_0);
          if (files_0.length) {
            const current_0 = input_0.value || [];
            const remaining_0 = MAX_FILES - current_0.length;
            if (remaining_0 > 0) {
              input_0.onChange([...current_0, ...files_0.slice(0, remaining_0)]);
            }
          }
        }}>{isTheft ? t("insuranceReport:form.documents.fields.policeReport") : t("insuranceReport:form.documents.fields.photos")}</UploadInput>{input_0.value && input_0.value.map((doc_0, index_0) => <DocumentRow key={`${doc_0.filename}-${index_0}`} document={doc_0} onDelete={() => {
          const next_0 = [...(input_0.value || [])];
          next_0.splice(index_0, 1);
          input_0.onChange(next_0);
        }} />)}{hasError_0 && <p className="mt-1 text-sm text-red-500">{errorMessage_0}</p>}</div>;
    };
    $[7] = isTheft;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <Field name={t4} validate={t5}>{t6}</Field>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t7) {
    t8 = <StepSection title={t2} anchorKey="insuranceReport.form.documents.title">{t3}{t7}</StepSection>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};